import {
  Area,
  AreaChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { euro, euroShort } from "../../lib/format";
import type { LoanResult } from "../../lib/finance";

/**
 * What you still owe, and what the loan has cost so far.
 *
 * Sampled yearly rather than monthly: 300 points would render the same shape
 * at this size while making every hover a search through a crowd.
 */
export function AmortisationChart({ loan }: { loan: LoanResult }) {
  if (loan.rows.length === 0) return null;

  const byYear: {
    year: number;
    balance: number;
    interest: number;
    insurance: number;
  }[] = [];
  let interest = 0;
  let insurance = 0;
  for (const r of loan.rows) {
    interest += r.interest;
    insurance += r.insurance;
    if (r.month % 12 === 0 || r.month === loan.rows.length) {
      byYear.push({ year: r.year, balance: r.balance, interest, insurance });
    }
  }

  return (
    <div className="h-32 w-full">
      <ResponsiveContainer>
        <AreaChart data={byYear} margin={{ top: 4, right: 4, bottom: 0, left: -6 }}>
          <XAxis
            dataKey="year"
            tick={{ fontSize: 10, fill: "#5a6472" }}
            axisLine={false}
            tickLine={false}
            minTickGap={16}
          />
          <YAxis
            tick={{ fontSize: 10, fill: "#5a6472" }}
            axisLine={false}
            tickLine={false}
            width={46}
            tickFormatter={euroShort}
          />
          <Tooltip
            contentStyle={{ fontSize: 11, borderRadius: 8, border: "1px solid #e3e7ed" }}
            labelFormatter={(y) => `Year ${y}`}
            formatter={(value, name) => [
              euro(Number(value)),
              name === "balance"
                ? "still owed"
                : name === "interest"
                  ? "interest paid"
                  : "insurance paid",
            ]}
          />
          <Area
            dataKey="balance"
            stroke="#2f6df6"
            fill="#2f6df6"
            fillOpacity={0.12}
            strokeWidth={2}
            isAnimationActive={false}
          />
          <Area
            dataKey="interest"
            stroke="#cf3a2e"
            fill="#cf3a2e"
            fillOpacity={0.08}
            strokeWidth={1.5}
            isAnimationActive={false}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}
